import { useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, XCircle, FileSpreadsheet } from "lucide-react";

const MASTERS = [
  { key: "zones", label: "Zones", cols: "zone_name" },
  { key: "branches", label: "Branches", cols: "branch_code, branch_name, zone_name, outlet_format (3S/2S/1S/Workshop/CPO/HO), branch_status (Live/Greenfield), district, launch_date" },
  { key: "departments", label: "Departments", cols: "dept_name, vertical (Sales/Aftersales/Support)" },
  { key: "designations", label: "Designations", cols: "desig_name, dept_name, grade, salary_band_min, salary_band_max, is_revenue_facing, approval_route" },
  { key: "users", label: "Users", cols: "full_name, email, role, employee_code, mobile, branch_scope (pipe-separated), dept_scope, zone_scope, vertical_scope" },
];

export default function ImportPage() {
  const [master, setMaster] = useState("branches");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const runPreview = async () => {
    if (!file) return toast.error("Choose a CSV or XLSX file first");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/import/${master}/preview`, fd);
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const valid = preview.rows.filter((r) => r.valid);
      const { data } = await api.post(`/import/${master}/commit`, { rows: valid });
      toast.success(`Imported ${data.inserted} rows (${data.skipped} skipped)`);
      setPreview(null);
      setFile(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const spec = MASTERS.find((m) => m.key === master);

  return (
    <div data-testid="import-page">
      <div className="mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Bulk Import</h1>
        <p className="text-sm text-[#6B7280] mt-1">Upload CSV or Excel. Every row is validated before anything is committed.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">Master</label>
            <select
              data-testid="import-master-select"
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#BF9B30]"
              value={master}
              onChange={(e) => { setMaster(e.target.value); setPreview(null); }}
            >
              {MASTERS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] uppercase tracking-wider mb-1.5">File (CSV / XLSX)</label>
            <input
              data-testid="import-file-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }}
              className="w-full text-sm file:mr-3 file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:rounded-sm file:cursor-pointer"
            />
          </div>
        </div>
        <p className="text-xs text-[#6B7280] mt-4 flex items-start gap-2">
          <FileSpreadsheet size={14} className="mt-0.5 shrink-0 text-[#BF9B30]" />
          Expected columns: {spec.cols}
        </p>
        <button
          data-testid="import-preview-button"
          onClick={runPreview}
          disabled={busy}
          className="mt-5 flex items-center gap-2 bg-[#0E2240] text-white text-sm px-5 py-2.5 rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-50"
        >
          <UploadCloud size={15} /> {busy ? "Validating…" : "Validate & Preview"}
        </button>
      </div>

      {preview && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <span data-testid="import-valid-count" className="flex items-center gap-1.5 text-sm text-[#1B7F3B] font-medium">
              <CheckCircle2 size={16} /> {preview.valid_count} valid
            </span>
            <span data-testid="import-error-count" className="flex items-center gap-1.5 text-sm text-[#BB162B] font-medium">
              <XCircle size={16} /> {preview.error_count} with errors
            </span>
            <button
              data-testid="import-commit-button"
              onClick={commit}
              disabled={busy || preview.valid_count === 0}
              className="ml-auto bg-[#1B7F3B] text-white text-sm px-5 py-2 rounded-sm hover:bg-[#156632] transition-colors duration-200 disabled:opacity-50"
            >
              Commit {preview.valid_count} valid rows
            </button>
          </div>
          <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
              <table className="w-full text-sm" data-testid="import-preview-table">
                <thead className="sticky top-0">
                  <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                    <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Row</th>
                    <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Status</th>
                    <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Data</th>
                    <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row_number} className={`border-b border-gray-100 last:border-0 ${r.valid ? "" : "bg-[#BB162B]/5"}`}>
                      <td className="px-4 py-2.5 tabular-nums text-xs">{r.row_number}</td>
                      <td className="px-4 py-2.5">
                        {r.valid
                          ? <span className="text-[#1B7F3B] text-xs font-medium">OK</span>
                          : <span className="text-[#BB162B] text-xs font-medium">Error</span>}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-[#6B7280] max-w-md truncate">
                        {Object.entries(r.data).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-[#BB162B]">{r.errors.join("; ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
